import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BreadCrumb from "components/common/BreadCrumb";
import { Container } from "reactstrap";
import { toast } from "react-toastify";
import { getMaterialInOut } from "features/MaterialInOuts/services/MaterialInOutService";
import { MaterialInOut } from "features/MaterialInOuts/types/material-in-out.types";
import { getDocumentTitle } from "utils/helpers";
import { MaterialInOutCreateModal } from "./MaterialInOutCreateModal";

const LIST_PATH = "/inventory-management/material-in-outs";

/**
 * Deep-link entry for editing a check-out row; uses the same modal as the
 * list. The list only surfaces the edit action while the row is still open
 * (dateIn == null), or for any row when the actor is Super Admin
 * (post-return editing) — this page has no client-side gate of its own; if
 * it's loaded directly for a returned row by anyone else, the update call
 * below is simply rejected by the server (surfaced via toast).
 */
const MaterialInOutForm = () => {
  document.title = getDocumentTitle("Edit material check-out");
  const navigate = useNavigate();
  const { id } = useParams();
  const [record, setRecord] = useState<MaterialInOut | null>(null);
  const [loadingRecord, setLoadingRecord] = useState(true);

  useEffect(() => {
    if (!id) {
      setLoadingRecord(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingRecord(true);
      try {
        const res = await getMaterialInOut(Number(id));
        if (!cancelled && res.success) {
          setRecord(res.data);
        } else if (!cancelled) {
          toast.error("Could not load record");
          navigate(LIST_PATH, { replace: true });
        }
      } catch (err) {
        console.warn(err);
        if (!cancelled) {
          toast.error("Could not load record");
          navigate(LIST_PATH, { replace: true });
        }
      } finally {
        if (!cancelled) setLoadingRecord(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const handleClose = () => {
    navigate(LIST_PATH, { replace: true });
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Edit material check-out"
          pageTitle="Inventory Management"
          backTo={{ label: "Material In/Out", path: LIST_PATH }}
        />
        {loadingRecord ? (
          <p className="text-muted">Loading…</p>
        ) : record ? (
          <MaterialInOutCreateModal
            show
            editingRecord={record}
            onClose={handleClose}
            onSuccess={handleClose}
          />
        ) : null}
      </Container>
    </div>
  );
};

export default MaterialInOutForm;
